import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { berserkr, getSupabase, isSupabaseConfigured } from "../lib/supabase";

export interface HornStatus {
  last_sounded: string | null;
  new_evidence_since: number;
}

/** Last horn run + how much evidence has landed since (drives the "re-sound?" nudge). */
export function useHornStatus(caseId: string | undefined) {
  return useQuery<HornStatus, Error>({
    queryKey: ["horn-status", caseId],
    enabled: isSupabaseConfigured() && !!caseId,
    staleTime: 30 * 1000,
    queryFn: async () => {
      if (!caseId) return { last_sounded: null, new_evidence_since: 0 };
      const { data, error } = await berserkr().rpc("fetch_horn_status", { p_case_id: caseId });
      if (error) throw new Error(error.message);
      return (data as HornStatus) ?? { last_sounded: null, new_evidence_since: 0 };
    },
  });
}

/**
 * Sound the Horn — re-run the whole timeline over currently-included evidence:
 * rebuild deterministic events (idempotent) + re-cluster chapters (LLM). On
 * success, refresh the Saga, Web, and horn status.
 */
export function useSoundTheHorn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (caseId: string) => {
      const sb = getSupabase();
      if (!sb) throw new Error("Supabase not configured");
      const { data, error } = await sb.functions.invoke("sound-the-horn", { body: { case_id: caseId } });
      if (error) throw new Error(error.message);
      return data as { status: string; episodes: number; chapters: number };
    },
    onSuccess: (_data, caseId) => {
      qc.invalidateQueries({ queryKey: ["case-timeline", caseId] });
      qc.invalidateQueries({ queryKey: ["case-web", caseId] });
      qc.invalidateQueries({ queryKey: ["horn-status", caseId] });
    },
  });
}
